import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, effect, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';

export type MenuMode = 'static' | 'overlay' | 'horizontal' | 'slim' | 'slim-plus' | 'reveal' | 'drawer';

export type ColorScheme = 'light' | 'dark';

export interface AppConfig {
  inputStyle: string;
  colorScheme: ColorScheme;
  componentTheme: string;
  ripple: boolean;
  menuMode: MenuMode;
  scale: number;
  menuTheme: string;
  topbarTheme: string;
  menuProfilePosition: string;
}

interface LayoutState {
  staticMenuMobileActive: boolean;
  overlayMenuActive: boolean;
  staticMenuDesktopInactive: boolean;
  configSidebarVisible: boolean;
  menuHoverActive: boolean;
  rightMenuActive: boolean;
  topbarMenuActive: boolean;
  menuProfileActive: boolean;
  sidebarActive: boolean;
  anchored: boolean;
}

@Injectable({ providedIn: 'root' })
export class LayoutService {
  _config: AppConfig = {
    ripple: true,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    componentTheme: environment.theme,
    scale: 16,
    menuTheme: 'light',
    topbarTheme: environment.theme,
    menuProfilePosition: 'end'
  };

  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    menuProfileActive: false,
    sidebarActive: false,
    anchored: false
  };

  config = signal<AppConfig>(this._config);

  private style: HTMLLinkElement;

  private cssFile: string;

  private themeCSSID = 'themeCSS';

  private configUpdate = new BehaviorSubject<AppConfig>(null);

  private overlayOpen = new Subject<any>();

  private topbarMenuOpen = new Subject<any>();

  private menuProfileOpen = new Subject<any>();

  private topbarMenuTitle = new Subject<any>();

  private menuActiveKey = new BehaviorSubject<any>(null);

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  topbarMenuOpen$ = this.topbarMenuOpen.asObservable();

  topbarMenuTitle$ = this.topbarMenuTitle.asObservable();

  menuProfileOpen$ = this.menuProfileOpen.asObservable();

  menuActiveKey$ = this.menuActiveKey.asObservable();

  constructor(@Inject(DOCUMENT) private document: Document) {
    effect(() => {
      const config = this.config();

      // if (this.updateStyle(config)) {
      //   this.changeTheme();
      // }

      this.changeScale(config.scale);
      this.onConfigUpdate();
    });
  }

  // updateStyle(config: AppConfig) {
  //   return config.componentTheme !== this._config.componentTheme || config.colorScheme !== this._config.colorScheme;
  // }

  // changeTheme() {
  //   const { colorScheme, componentTheme } = this.config();
  //   const themeLink = <HTMLLinkElement>document.getElementById('theme-link');
  //   const themeLinkHref = themeLink.getAttribute('href')!;
  //   const newHref = themeLinkHref
  //     .split('/')
  //     .map((el) =>
  //       el == this._config.componentTheme
  //         ? (el = componentTheme)
  //         : el == `theme-${this._config.colorScheme}`
  //           ? (el = `theme-${colorScheme}`)
  //           : el
  //     )
  //     .join('/');
  //   this.replaceThemeLink(newHref);
  // }

  // replaceThemeLink(href: string) {
  //   const id = 'theme-link';
  //   const themeLink = <HTMLLinkElement>document.getElementById(id);
  //   const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

  //   cloneLinkElement.setAttribute('href', href);
  //   cloneLinkElement.setAttribute('id', id + '-clone');

  //   themeLink.parentNode!.insertBefore(cloneLinkElement, themeLink.nextSibling);
  //   cloneLinkElement.addEventListener('load', () => {
  //     themeLink.remove();
  //     cloneLinkElement.setAttribute('id', id);
  //   });
  // }

  changeScale(value: number) {
    document.documentElement.style.fontSize = `${value}px`;
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;

      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isTablet()) {
      this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    } else if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  onTopbarMenuToggle() {
    this.state.topbarMenuActive = !this.state.topbarMenuActive;
    if (this.state.topbarMenuActive) {
      this.topbarMenuOpen.next(null);
    }
  }

  onOverlaySubmenuOpen() {
    this.overlayOpen.next(null);
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 576;
  }

  isSlim(mode?: MenuMode) {
    if (mode) {
      return mode === 'slim';
    } else {
      return this.config().menuMode === 'slim';
    }
  }

  isSlimPlus(mode?: MenuMode) {
    return mode ? mode === 'slim-plus' : this.config().menuMode === 'slim-plus';
  }

  isHorizontal(mode?: MenuMode) {
    return mode ? mode === 'horizontal' : this.config().menuMode === 'horizontal';
  }

  isMobile() {
    return !this.isDesktop();
  }

  isTablet() {
    return window.innerWidth >= 576 && window.innerWidth < 992;
  }

  onConfigUpdate() {
    this._config = { ...this.config() };
    this.configUpdate.next(this.config());
  }

  isRightMenuActive(): boolean {
    return this.state.rightMenuActive;
  }

  openRightSidebar(): void {
    this.state.rightMenuActive = true;
  }

  onMenuProfileToggle() {
    this.state.menuProfileActive = !this.state.menuProfileActive;
    if (this.state.menuProfileActive && this.isHorizontal() && this.isDesktop()) {
      this.menuProfileOpen.next(null);
    }
  }

  updateTopbarTitle(title: string) {
    this.topbarMenuTitle.next(title);
  }

  updatedMenuActiveKey(key: string) {
    this.menuActiveKey.next(key);
  }

  setTheme(theme: any, renderer2: Renderer2) {
    this.cssFile = `${theme}.css`;
    this.removeExistingThemeStyle(renderer2, this.themeCSSID);

    // Create a link element via Angular's renderer to avoid SSR troubles
    this.style = renderer2.createElement('link') as HTMLLinkElement;

    // Set type of the link item and path to the css file
    renderer2.setProperty(this.style, 'rel', 'stylesheet');
    renderer2.setProperty(this.style, 'href', this.cssFile);
    renderer2.setProperty(this.style, 'id', this.themeCSSID);

    // Add the style to the head section
    renderer2.appendChild(this.document.head, this.style);
  }

  removeExistingThemeStyle(renderer2: Renderer2, themeCSSID: string) {
    const themeIDHTMlElem = this.document.getElementById(themeCSSID);
    if (themeIDHTMlElem) {
      renderer2.removeChild(this.document.head, themeIDHTMlElem);
    }
  }
}
